<template>
    <div class="monitor-page" style="min-height: 90vh; background-size: 360px; background-position: right bottom">
        <!-- Spinner centralizado -->
        <div v-if="isLoadingAttendanceEventButton || isLoadingEvents" class="loading-overlay">
            <i class="fas fa-spinner fa-spin loading-icon"></i>
        </div>
        <!-- Header fixo com título + StatsBar -->
        <div class="monitor-sticky-header">
            <div class="monitor-sticky-inner">
                <h1 class="page-header mb-0 monitor-title">{{ $t('str.form.title.monitor') }}</h1>
                <div class="monitor-statsbar-inline">
                    <StatsBar
                        :totalEvents="totalEvents"
                        :attendedEvents="attendedEvents"
                        :sosEvents="sosEvents"
                        :incidentEvents="incidentEvents"
                        :cancelPatrolEvents="cancelPatrolEvents"
                        :failurePatrolEvents="failurePatrolEvents"
                        :enabledMonitorEventTypes="enabledMonitorEventTypes"
                        :isLoadingEventTypes="isLoadingCompanySettings"
                        :activeTypeFilter="activeTypeFilter"
                        @filter-type-selected="onFilterTypeSelected"
                    />
                </div>
            </div>
        </div>

        <!-- UI técnica de chamada ocultada; atendimento agora é feito via modal dedicada -->
        <audio ref="remoteAudio" autoplay class="d-none"></audio>

        <transition name="call-modal-fade">
            <div v-if="callModalVisible" class="call-modal-backdrop">
                <div class="call-modal-shell">
                    <div class="call-modal-card" :class="{ 'is-ringing': isIncomingCallRinging }">
                        <div class="call-modal-glow"></div>
                        <div class="call-modal-header">
                            <div class="call-modal-avatar">
                                <i :class="callPeerIconClass"></i>
                            </div>

                            <div class="call-modal-header-info">
                                <p class="call-modal-overline">{{ callModalOverline }}</p>
                                <h4 class="call-modal-name">{{ callPeerDisplayName }}</h4>
                                <p class="call-modal-peer-type">{{ callPeerTypeLabel }}</p>
                            </div>

                            <span class="call-modal-state" :class="{ ringing: isIncomingCallRinging, connected: isCallConnected }">
                                {{ callConnectionStateLabel }}
                            </span>
                        </div>

                        <div class="call-modal-body">
                            <div class="call-modal-timer">{{ formattedCallDuration }}</div>
                            <div class="call-modal-status">
                                {{ callModalStatusText }}
                            </div>
                        </div>

                        <div v-if="isIncomingCallRinging" class="call-modal-actions">
                            <button type="button" class="call-action-btn reject" :disabled="callActionBusy" @click="rejectIncomingCall">
                                <i class="fa fa-times"></i>
                                <span>Rejeitar</span>
                            </button>
                            <button type="button" class="call-action-btn accept" :disabled="callActionBusy" @click="acceptIncomingCall">
                                <i class="fa fa-phone"></i>
                                <span>Atender</span>
                            </button>
                        </div>

                        <div v-else class="call-modal-actions active-call">
                            <button
                                type="button"
                                class="call-action-btn control"
                                :class="{ active: callMicMuted || isSilentListenMicLocked }"
                                :disabled="isSilentListenMicLocked"
                                @click="toggleCallMicMute"
                            >
                                <i :class="isSilentListenMicLocked || callMicMuted ? 'fa fa-microphone-slash' : 'fa fa-microphone'"></i>
                                <span>
                                    {{ isSilentListenMicLocked ? 'Microfone bloqueado (escuta ativa)' : callMicMuted ? 'Ativar microfone' : 'Mutar microfone' }}
                                </span>
                            </button>

                            <button type="button" class="call-action-btn control" :class="{ active: callRemoteMuted }" @click="toggleRemoteAudioMute">
                                <i :class="callRemoteMuted ? 'fa fa-volume-mute' : 'fa fa-volume-up'"></i>
                                <span>{{ callRemoteMuted ? 'Ativar áudio' : 'Silenciar áudio' }}</span>
                            </button>

                            <button type="button" class="call-action-btn end" :disabled="callActionBusy" @click="endCall">
                                <i class="fa fa-phone"></i>
                                <span>Encerrar</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </transition>

        <div class="row mt-2">
            <div class="col-md-12 mb-3">
                <div id="accordion" class="accordion rounded overflow-hidden">
                    <b-card class="bg-gray-800 text-white border-0 rounded-0" no-body>
                        <b-card-header header-tag="header" class="card-header bg-gray-900 text-white pointer-cursor px-3 py-10px d-flex align-items-center" v-b-toggle.companies>
                            <div class="filter-title">
                                {{ $t('str.filters') }}
                            </div>
                        </b-card-header>

                        <b-collapse id="companies" visible accordion="filterSession">
                            <b-card-body>
                                <div class="row">
                                    <div v-if="isSuperAdminMaster" class="col-md-2 mb-3">
                                        <label class="form-label" for="accountField">
                                            {{ $t('str.register.user.account.field') }}
                                        </label>
                                        <select v-model="filters.account" @change="changeAccount" class="form-select" id="accountField">
                                            <option value="">{{ $t('str.register.select.placeholder') }}</option>
                                            <option v-for="account in accounts" :value="account._id" :key="account._id">
                                                {{ account.name }}
                                            </option>
                                        </select>
                                    </div>
                                    <div v-if="isSuperAdminMaster || role === 'ADMIN' || role === 'MANAGER'" class="col-md-2 mb-3">
                                        <label class="form-label" for="clientField">
                                            {{ $t('str.register.user.client.field') }}
                                        </label>
                                        <select v-model="filters.client" @change="changeClient" class="form-select" id="clientField">
                                            <option value="">{{ $t('str.register.select.placeholder') }}</option>
                                            <option v-for="client in clients" :value="client._id" :key="client._id">
                                                {{ client.name }}
                                            </option>
                                        </select>
                                    </div>
                                    <div class="col-md-2 mb-3">
                                        <label class="form-label" for="siteField">
                                            {{ $t('str.register.user.site.field') }}
                                        </label>
                                        <select v-model="filters.site" @change="filter" class="form-select" id="siteField">
                                            <option value="">{{ $t('str.monitor.filters.site.all') }}</option>
                                            <option v-for="site in sites" :value="site._id" :key="site._id">
                                                {{ site.name }}
                                            </option>
                                        </select>
                                    </div>
                                </div>
                            </b-card-body>
                        </b-collapse>
                    </b-card>
                </div>
            </div>
        </div>

        <div v-if="events && events?.length > 0" class="row">
            <!-- Lista de eventos -->
            <div v-if="events?.length > 0" class="col-xl-4 col-lg-6">
                <panel :title="$t('str.events.title')">
                    <div ref="eventListContainer" class="list-group list-group-flush rounded-bottom overflow-hidden panel-body p-0" @scroll="onEventsListScroll">
                        <div @click="handleSelectEvent(event)" v-for="event in displayedEvents" :key="event?._id || event?.id">
                            <a class="list-group-item list-group-item-action d-flex cursor-mouse" :class="selectedEvent?._id === event?._id ? 'list-group-selected' : ''">
                                <div class="me-3 fs-16px">
                                    <i :class="event?.icon" />
                                </div>
                                <div class="flex-fill">
                                    <div class="fs-14px lh-12 mb-2px fw-bold">
                                        {{ event?.failureText }}
                                    </div>
                                    <div class="mb-1 fs-12px">
                                        <div class="flex-1">{{ event?.description }}</div>
                                    </div>
                                    <div class="mb-1">
                                        <span :class="event?.tag">
                                            {{ event?.failurePatrolType ? $t('str.msg.failure.patrol') : $t(event?.type) }}
                                        </span>
                                    </div>
                                </div>
                                <div v-if="!event?.attendance || !event?.attendance?.isAttendance" class="dot animated-dot">
                                    <i class="fa fa-circle dot-color" />
                                </div>
                            </a>
                        </div>
                        <div v-if="isLoadingMoreEvents" class="text-center py-2">
                            <i class="fas fa-spinner fa-spin text-white"></i>
                        </div>
                        <div v-else-if="!allEventsLoaded && events.length > 0" class="text-center py-2">
                            <a class="cursor-mouse text-muted small" @click="loadMoreEvents">{{ $t('str.monitor.load.more') }}</a>
                        </div>
                    </div>
                </panel>
            </div>

            <!-- Evento selecionado -->
            <div v-if="selectedEvent" class="col-xl-4 col-lg-6">
                <panel :title="selectedEvent?.failureText + ' ' + $t('str.on') + ' ' + selectedEvent?.formattedDate + ' ' + $t('str.at') + ' ' + selectedEvent?.formattedTime">
                    <div class="result-info">
                        <div class="flex-1 mb-3">
                            <div class="text-opacity-50 small fw-bold">
                                {{ $t('str.monitor.details.patrol') }}:
                                <label v-if="selectedEvent?.event?.name" class="info-result">
                                    {{ selectedEvent?.event?.name }}
                                </label>
                                <label v-else class="info-result">
                                    {{ $t('str.monitor.alone.patrol') }}
                                </label>
                            </div>
                            <div class="text-opacity-50 small fw-bold">
                                {{ $t('str.monitor.details.status') }}:
                                <label v-if="selectedEvent?.event" class="info-result">
                                    {{ $t(selectedEvent?.event?.status) }}
                                </label>
                                <label v-else class="info-result">
                                    {{ $t('str.monitor.without.status') }}
                                </label>
                            </div>
                            <div class="text-opacity-50 small fw-bold">
                                {{ $t('str.monitor.details.site') }}:
                                <label class="info-result">
                                    {{ selectedEvent?.site?.name }}
                                </label>
                            </div>
                        </div>
                        <hr class="bg-gray-500" />

                        <div v-if="outsideGeofenceDetails" class="flex-1 mb-3">
                            <div class="text-opacity-50 small fw-bold">
                                {{ $t('str.monitor.details.geofence.point') }}:
                                <label class="info-result">{{ outsideGeofenceDetails.point }}</label>
                            </div>
                            <div class="text-opacity-50 small fw-bold">
                                {{ $t('str.monitor.details.geofence.vigilant') }}:
                                <label class="info-result">{{ outsideGeofenceDetails.vigilant }}</label>
                            </div>
                            <div class="text-opacity-50 small fw-bold">
                                {{ $t('str.monitor.details.geofence.distance') }}:
                                <label class="info-result">{{ outsideGeofenceDetails.distance }}</label>
                            </div>
                            <div class="text-opacity-50 small fw-bold">
                                {{ $t('str.monitor.details.geofence.radius') }}:
                                <label class="info-result">{{ outsideGeofenceDetails.radius }}</label>
                            </div>
                            <div class="text-opacity-50 small fw-bold">
                                {{ $t('str.monitor.details.geofence.accuracy') }}:
                                <label class="info-result">{{ outsideGeofenceDetails.accuracy }}</label>
                            </div>
                            <div class="text-opacity-50 small fw-bold">
                                {{ $t('str.monitor.details.geofence.time') }}:
                                <label class="info-result">{{ outsideGeofenceDetails.time }}</label>
                            </div>
                        </div>
                        <hr v-if="outsideGeofenceDetails" class="bg-gray-500" />

                        <div v-if="selectedEvent?.vigilant" class="flex-1 mb-3">
                            <div class="timeline-header">
                                <div class="userimage">
                                    <img
                                        v-if="selectedEvent?.vigilant?.photoURL && selectedEvent?.vigilant?.photoURL !== 'https://'"
                                        crossorigin="anonymous"
                                        :src="`${domain}${selectedEvent?.vigilant?.photoURL}`"
                                    />
                                    <img v-else src="@/assets/images/profile.png" />
                                </div>
                                <div class="username">
                                    <a> {{ selectedEvent?.vigilant?.firstName }} {{ selectedEvent?.vigilant?.lastName }} </a>
                                </div>
                            </div>

                            <div class="d-flex justify-content-between align-items-center mb-1">
                                <div class="text-opacity-50 small fw-bold mb-0">
                                    {{ $t('str.monitor.details.vigilant.phone') }}:
                                    <label class="info-result">
                                        {{ selectedEvent?.vigilant?.primaryPhone }}
                                    </label>
                                </div>
                                <div class="text-opacity-50 small fw-bold mb-0">
                                    <span :class="['vigilant-connection-badge', isSelectedVigilantOnline ? 'is-online' : 'is-offline']">
                                        <i class="fa fa-circle"></i>
                                        {{ isSelectedVigilantOnline ? 'Online' : 'Offline' }}
                                    </span>
                                </div>
                            </div>
                            <div v-if="selectedEvent?.vigilant?.email" class="text-opacity-50 small fw-bold mt-2">
                                {{ $t('str.monitor.details.vigilant.email') }}:
                                <label class="info-result">
                                    {{ selectedEvent?.vigilant?.email }}
                                </label>
                            </div>
                        </div>
                        <hr v-if="selectedEvent?.vigilant" class="bg-gray-500" />

                        <div v-if="selectedEvent?.attendance?.isAttendance" class="flex-1 mb-3">
                            <div class="text-opacity-50 small fw-bold">
                                {{ $t('str.monitor.details.attendance.by') }}:
                                <label class="info-result">
                                    {{ selectedEvent?.attendance?.operator?.firstName }}
                                    {{ selectedEvent?.attendance?.operator?.lastName }}
                                </label>
                            </div>
                            <div class="text-opacity-50 small fw-bold">
                                {{ $t('str.monitor.details.attendance.opened.date') }}:
                                <label class="info-result">
                                    {{ formatDate(selectedEvent?.attendance?.openedDate) }}
                                </label>
                            </div>
                            <div v-if="selectedEvent?.attendance?.closedDate" class="text-opacity-50 small fw-bold">
                                {{ $t('str.monitor.details.attendance.closed.date') }}:
                                <label class="info-result">
                                    {{ formatDate(selectedEvent?.attendance?.closedDate) }}
                                </label>
                            </div>
                            <hr class="bg-gray-500" />
                        </div>

                        <div class="btn-row mb-4" v-if="selectedEvent.type !== 'FAILURE_PATROL'">
                            <a v-if="selectedEvent?.geolocation && selectedEvent?.geolocation?.latitude" @click="showMap" data-toggle="tooltip" data-container="body" data-title="Map" class="cursor-mouse">
                                <i class="fa fa-fw fa-map-marker-alt"></i>
                            </a>
                            <a v-if="selectedEvent?.photoURL && selectedEvent?.photoURL?.length > 0" @click="showPhoto()" data-toggle="tooltip" data-container="body" data-title="Image" class="cursor-mouse">
                                <i class="fa fa-fw fa-image"></i>
                                <i v-if="selectedEvent?.photoURL === 'https://'" class="fa fa-fw fa-clock small-icon"></i>
                            </a>
                            <a v-if="selectedEvent?.soundURL && selectedEvent?.soundURL?.length > 0" @click="showSound()" data-toggle="tooltip" data-container="body" data-title="Audio" class="cursor-mouse">
                                <i class="fa fa-fw fa-volume-up"></i>
                                <i v-if="selectedEvent?.soundURL === 'https://'" class="fa fa-fw fa-clock small-icon"></i>
                            </a>
                            <a
                                v-if="selectedEvent?.signatureURL && selectedEvent?.signatureURL?.length > 0"
                                @click="showSignature()"
                                data-toggle="tooltip"
                                data-container="body"
                                data-title="Signature"
                                class="cursor-mouse"
                            >
                                <i class="fa fa-fw fa-pen"></i>
                                <i v-if="selectedEvent?.signatureURL === 'https://'" class="fa fa-fw fa-clock small-icon"></i>
                            </a>
                            <a v-if="selectedEvent?.deviceInfo" @click="showDeviceInfo()" data-toggle="tooltip" data-container="body" data-title="Device Info" class="cursor-mouse">
                                <i class="fa fa-fw fa-mobile-alt"></i>
                            </a>
                        </div>

                        <div v-if="canShowEventCallActions" class="event-call-card mb-3">
                            <div class="event-call-card-header">
                                <span class="event-call-card-title">Comunicação em tempo real</span>
                                <small class="event-call-card-subtitle">Disponível somente para quem iniciou este atendimento</small>
                            </div>

                            <div class="event-call-grid">
                                <button type="button" class="event-call-btn primary" :disabled="isStartingEventCall || !canCallVigilant" @click="startEventCall('VIGILANT', 'NORMAL')">
                                    <i class="fa fa-phone"></i>
                                    <span>{{ isStartingEventCall ? 'Iniciando ligação...' : 'Ligar para vigilante' }}</span>
                                </button>

                                <button
                                    v-if="canShowSilentListenAction"
                                    type="button"
                                    class="event-call-btn warning"
                                    :disabled="isStartingEventCall || !canCallVigilant"
                                    @click="startEventCall('VIGILANT', 'SILENT_LISTEN')"
                                >
                                    <i class="fa fa-headphones"></i>
                                    <span>{{ isStartingEventCall ? 'Iniciando escuta...' : 'Escuta ativa (SOS)' }}</span>
                                </button>
                            </div>
                        </div>

                        <div v-if="selectedEvent?.incidents?.length > 0" class="mb-3">
                            <div class="text-opacity-50 small fw-bold">{{ $t('str.monitor.details.incidents') }}:</div>
                            <div class="text-opacity-50 small fw-bold">
                                <ul>
                                    <li v-for="incident in selectedEvent?.incidents" :key="incident._id">
                                        {{ incident.name }}
                                    </li>
                                </ul>
                            </div>
                        </div>

                        <div v-if="canStartSelectedEventAttendance" class="result-price">
                            <a
                                @click="handleAttendanceEvent()"
                                class="btn d-block w-100"
                                :class="selectedEventAttendanceButtonClass"
                            >
                                <i v-if="isLoadingAttendanceEventButton" class="fas fa-spinner fa-spin"></i>
                                {{ $t('str.monitor.answer.event') }}
                            </a>
                        </div>
                    </div>
                </panel>
            </div>

            <!-- Atendimentos do evento selecionado -->
            <div class="col-xl-4 col-lg-6 panel-attendance-body p-0" v-if="selectedEvent?.attendance?.isAttendance">
                <panel :title="$t('str.event.attendance')">
                    <div v-if="isSelectedEventAttendanceOwner && selectedEvent?.attendance?.status === 'IN_PROGRESS'" class="result-info overflow-hidden">
                        <div class="flex-1 mb-3">
                            <label class="form-label" for="attendancesOptionsField">
                                {{ $t('str.attendances.options.field') }}
                            </label>
                            <select
                                v-model="attendance.type"
                                class="form-select"
                                id="attendancesOptionsField"
                                @focus="removeRequiredField('attendanceOptions')"
                                :class="checkRequiredField('attendanceOptions') ? 'is-invalid' : ''"
                            >
                                <option value="">{{ $t('str.register.select.placeholder') }}</option>
                                <option v-for="attendance in attendancesTypes" :value="attendance._id" :key="attendance._id">
                                    {{ $t(attendance._id) }}
                                </option>
                            </select>
                            <div class="invalid-feedback">
                                {{ $t('str.attendances.options.field.error') }}
                            </div>
                        </div>
                        <div class="flex-1 mb-3">
                            <label class="form-label" for="attendancesNotesField">
                                {{ $t('str.attendances.notes.field') }}
                            </label>
                            <textarea v-model="attendance.notes" class="form-control" id="attendancesNotesField" rows="3"></textarea>
                        </div>

                        <div class="result-price d-flex justify-content-between align-items-center mb-3">
                            <a
                                @click="checkForm()"
                                class="btn d-block w-50 m-2"
                                :class="selectedEventAttendanceButtonClass"
                            >
                                <i v-if="isLoadingSendAttendanceButton" class="fas fa-spinner fa-spin"></i>
                                {{ $t('str.btn.attendance.register') }}
                            </a>
                            <a @click="confirmCloseAttendance" class="btn d-block w-50 m-2 btn-gray">
                                {{ $t('str.btn.attendance.close') }}
                            </a>
                        </div>
                        <hr class="bg-gray-500" />
                    </div>

                    <div class="result-info pd-0">
                        <label class="form-label">
                            {{ $t('str.attendances.history.list') }}
                        </label>
                        <table class="table table-hover table-striped table-bordered">
                            <thead>
                                <tr>
                                    <th scope="col">{{ $t('str.attendances.date.field') }}</th>
                                    <th scope="col">{{ $t('str.attendances.type.field') }}</th>
                                    <th width="30%" scope="col">
                                        {{ $t('str.attendances.notes.field') }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-if="isLoadingAttendanceList">
                                    <td colspan="3" class="text-center">
                                        <i class="fas fa-spinner fa-spin"></i>
                                    </td>
                                </tr>
                                <tr v-else-if="!isLoadingAttendanceList && attendances.length === 0">
                                    <td colspan="3" class="text-center">
                                        {{ $t('str.msg.waiting.attendance.by.operator') }}
                                    </td>
                                </tr>
                                <tr v-else v-for="attendance in attendances" :key="attendance._id">
                                    <td>{{ formatDate(attendance.createDate) }}</td>
                                    <td>{{ $t(attendance.type) }}</td>
                                    <td>{{ attendance.notes }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </panel>
            </div>

            <gritterNotify v-if="operatorWithoutGroup" :title="$t('str.operator.without.sites.title')" :text="$t('str.operator.without.sites.text')" @after-close="operatorWithoutGroup = false" />
        </div>
        <div v-else>
            <div class="no-events">
                <div>
                    <i class="fa fa-exclamation-triangle fa-5x text-white mb-4"></i>
                    <h3 class="info-title">{{ $t('str.monitor.no.events.title') }}</h3>
                    <p>{{ $t('str.monitor.no.events.description') }}</p>
                </div>
            </div>
        </div>

        <notifications group="top-right" position="top right" :speed="1000" />
        <Photo :photoURL="selectedEvent?.photoURL" />
        <Signature :signatureURL="selectedEvent?.signatureURL" />
        <Sound :soundURL="selectedEvent?.soundURL" />
        <Map :data="selectedEvent" />
        <DeviceInfo :data="selectedEvent" />
    </div>
</template>

<script>
import Vue from 'vue'

import Common from '../../common/Common.vue'
import Services from '../../common/Services.vue'
import gritterNotify from '../../components/notification/gritter.vue'
import { db } from '../../firebaseInit.js'
import { createDefaultMonitorEventFilters } from '../../utils/events.js'
import DeviceInfo from './Components/DeviceInfo/DeviceInfo.vue'
import Map from './Components/Map/Map.vue'
import Photo from './Components/Photo/Photo.vue'
import Signature from './Components/Signature/Signature.vue'
import Sound from './Components/Sound/Sound.vue'
import StatsBar from './Components/StatsBar/StatsBar.vue'
import Controller from './CrtMonitor.vue'
// ajuste o caminho conforme sua estrutura de pastas
import { deleteDoc, doc, onSnapshot } from 'firebase/firestore'
import moment from 'moment'
import socket from '../../config/websocket.js'
import Endpoints from '../../common/Endpoints.vue'
import phoneRingtoneUrl from '../../assets/sound/phone-ringtone.wav'
import { clearPendingIncomingCall, consumePendingIncomingCall, subscribePendingIncomingCall } from '../../services/operatorCallBridge'
import {
    attachStreamToRecording,
    createCallRecordingState,
    resetCallRecordingState,
    setCallRecordingPolicy,
    startCallRecording,
    stopCallRecordingAndUpload,
} from '../../services/callRecordingBridge'
import { getWebRtcIceServers } from '../../services/webrtcIceConfig'

Vue.prototype.$registerEvent = new Vue()

const OPERATOR_ATTENDABLE_EVENT_TYPES = ['SOS_ALERT', 'FAILURE_PATROL', 'INCIDENT', 'CANCEL_PATROL']

const EVENT_ATTENDANCE_BUTTON_CLASS_BY_TYPE = {
    INCIDENT: 'btn-yellow',
    SOS_ALERT: 'btn-red',
    FAILURE_PATROL: 'btn-dark-orange',
    CANCEL_PATROL: 'btn-indigo',
}

export default {
    components: {
        Map,
        Photo,
        Signature,
        Sound,
        DeviceInfo,
        gritterNotify,
        StatsBar,
    },
    data() {
        return {
            isLoading: false,
            isLoadingEvents: false,
            isLoadingSendAttendanceButton: false,
            isLoadingAttendanceEventButton: false,
            isLoadingAttendanceList: false,
            domain: '',
            valuekey: 0,
            errors: [],
            accounts: [],
            clients: [],
            sites: [],
            items: [],
            events: [],
            user: null,
            role: null,
            attendances: [],
            selectedEvent: null,
            selectedAttendence: null,
            attendancesTypes: [],
            attendanceEventEnabled: false,
            operatorWithoutGroup: false,
            monitorEventFilters: createDefaultMonitorEventFilters(),
            companySettings: null,
            isLoadingCompanySettings: false,
            attendance: {
                account: null,
                client: null,
                site: null,
                operator: null,
                event: '',
                type: '',
                notes: '',
                patrolAction: '',
                createDate: moment().utc().format(),
                status: 'ACTIVE',
            },
            answerEvent: null,
            filters: {
                types: [],
                status: 'ACTIVE',
                account: '',
                client: '',
                site: '',
                sites: [],
                eventNotAttandance: true,
                useExactDateRange: true,
                startDate: moment().subtract(3, 'hours').format(),
                endDate: moment().format(),
            },
            isSuperAdminMaster: false,
            subscribes: [],
            activeTypeFilter: null,
            monitorPage: 1,
            isLoadingMoreEvents: false,
            allEventsLoaded: false,
            totalEventsCount: 0,

            // ms-chat / WebRTC call state (web caller)
            socketConnected: false,
            socketUserId: '',
            onlineUsers: [],
            callTargetUserId: '',
            callStatus: '',
            activeCallRoomId: '',
            pendingIncomingCall: null,
            incomingCallAccepted: false,
            callDuration: 0,
            callPc: null,
            callLocalStream: null,
            callPendingIce: [],
            callPendingOffer: null,
            incomingRingtoneAudio: null,
            socketHandlers: null,
            socketMsChatUrl: '',
            callPeerUserId: '',
            callPeerLabel: '',
            callPeerClientType: '',
            callPeerRole: '',
            callMicMuted: false,
            callRemoteMuted: false,
            callActionBusy: false,
            callMode: 'NORMAL',
            isStartingEventCall: false,
            operatorIncomingCallBridgeUnsubscribe: null,
            callRecordingState: createCallRecordingState(),
        }
    },
    methods: {
        ...Controller.methods,

        onFilterTypeSelected(type) {
            if (this.activeTypeFilter === type) {
                this.activeTypeFilter = null
            } else {
                this.activeTypeFilter = type
            }
        },

        resolveCallRecordingPolicy(payloadPolicy) {
            if (typeof payloadPolicy === 'boolean') return payloadPolicy
            return this.companySettings?.monitor?.callRecordingEnabled === true
        },

        applyCallRecordingPolicy(payloadPolicy) {
            const enabled = this.resolveCallRecordingPolicy(payloadPolicy)
            setCallRecordingPolicy(this.callRecordingState, enabled)
        },

        onEventsListScroll() {
            const container = this.$refs.eventListContainer
            if (!container) return
            const nearBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 50
            if (nearBottom && !this.isLoadingMoreEvents && !this.allEventsLoaded) {
                this.loadMoreEvents()
            }
        },

        initCallSocket() {
            if (this.socketHandlers) return

            // Mostra a URL efetiva do Socket.IO (debug visual rápido)
            try {
                this.socketMsChatUrl = socket?.io?.uri || Endpoints?.socketDomain || ''
            } catch (e) {
                this.socketMsChatUrl = ''
            }

            const onConnect = () => {
                this.socketConnected = true
                this.registerOnSocket()
            }
            const onDisconnect = () => {
                this.socketConnected = false
                this.stopIncomingRingtone()
                if (this.activeCallRoomId || this.pendingIncomingCall) {
                    this.callStatus = 'Conexão com o chat foi perdida'
                    this.cleanupCall()
                }
            }

            const onUserList = ({ users }) => {
                this.onlineUsers = Array.isArray(users) ? users : []
            }

            const onCallTick = ({ roomId, elapsed }) => {
                if (this.activeCallRoomId && roomId === this.activeCallRoomId) this.callDuration = elapsed
            }

            const onCallAccept = ({ roomId, userId }) => {
                if (this.activeCallRoomId && roomId === this.activeCallRoomId) {
                    this.stopIncomingRingtone()
                    this.callStatus = 'Chamada em andamento'
                    if (!this.callPeerUserId && userId) this.callPeerUserId = userId
                }
            }

            const onCallIncoming = ({ roomId, from, fromLabel, fromClientType, fromRole, fromDisplayName, callMode }) => {
                // Ligações originadas do alertport-app são tratadas pela tela /monitor/alert-occurrence.
                // Aqui (vigilância) ignoramos silenciosamente para não sobrepor o modal de chamada
                // nem rejeitar a chamada no servidor — o AlertMonitor é quem aceita/rejeita.
                const normalizedClientType = String(fromClientType || '').toUpperCase()
                if (normalizedClientType.includes('ALERTPORT')) {
                    return
                }
                this.applyIncomingCallPayload({ roomId, from, fromLabel, fromClientType, fromRole, fromDisplayName, callMode }, { rejectIfBusy: true, clearBridge: true })
            }

            const onCallReject = ({ roomId, userId }) => {
                if (this.activeCallRoomId && roomId === this.activeCallRoomId) {
                    this.stopIncomingRingtone()
                    this.callStatus = `Rejeitada por ${userId}`
                    clearPendingIncomingCall(roomId)
                    this.cleanupCall()
                }
            }

            const onCallEnd = ({ roomId, reason, duration }) => {
                if (this.activeCallRoomId && roomId === this.activeCallRoomId) {
                    this.stopIncomingRingtone()
                    this.callStatus = reason ? `Encerrada (${reason})` : `Encerrada (${duration ?? 0}s)`
                    clearPendingIncomingCall(roomId)
                    this.cleanupCall()
                }
            }

            const onAnswer = async ({ roomId, sdp }) => {
                if (!this.callPc || !this.activeCallRoomId || roomId !== this.activeCallRoomId) return
                try {
                    // Some browsers are picky about the exact type passed here.
                    const SessionDesc = window.RTCSessionDescription || window.webkitRTCSessionDescription || window.mozRTCSessionDescription
                    const desc = SessionDesc ? new SessionDesc(sdp) : sdp
                    await this.callPc.setRemoteDescription(desc)
                    this.callStatus = 'Conectando áudio...'
                } catch (e) {
                    this.callStatus = `Erro ao aplicar answer: ${String(e?.message || e)}`
                }
            }

            const onOffer = async ({ roomId, from, sdp }) => {
                if (!this.activeCallRoomId || roomId !== this.activeCallRoomId) return
                if (this.pendingIncomingCall && !this.incomingCallAccepted) {
                    this.callPendingOffer = { roomId, from, sdp }
                    return
                }

                await this.processOffer({ roomId, from, sdp })
            }

            const onIce = async ({ roomId, candidate }) => {
                if (!this.activeCallRoomId || roomId !== this.activeCallRoomId) return
                if (!this.callPc) {
                    this.callPendingIce.push(candidate)
                    return
                }
                try {
                    const IceCand = window.RTCIceCandidate || window.webkitRTCIceCandidate || window.mozRTCIceCandidate
                    const ice = IceCand ? new IceCand(candidate) : candidate
                    await this.callPc.addIceCandidate(ice)
                } catch (e) {
                    // ignore candidate race
                }
            }

            this.socketHandlers = {
                onConnect,
                onDisconnect,
                onUserList,
                onCallTick,
                onCallAccept,
                onCallIncoming,
                onCallReject,
                onCallEnd,
                onOffer,
                onAnswer,
                onIce,
            }

            socket.on('connect', onConnect)
            socket.on('disconnect', onDisconnect)
            socket.on('user:list', onUserList)
            socket.on('call:duration:tick', onCallTick)
            socket.on('call:accept', onCallAccept)
            socket.on('call:incoming', onCallIncoming)
            socket.on('call:reject', onCallReject)
            socket.on('call:end', onCallEnd)
            socket.on('webrtc:offer', onOffer)
            socket.on('webrtc:answer', onAnswer)
            socket.on('webrtc:ice', onIce)

            this.socketConnected = socket.connected
            if (socket.connected) this.registerOnSocket()
        },

        registerOnSocket() {
            const userId = this.getSocketUserId()
            if (!userId) {
                this.callStatus = 'Usuário não identificado para registrar no ms-chat.'
                return
            }

            const accountId = this.getSocketAccountId()
            const role = this.getSocketRole()
            if (!accountId) {
                this.callStatus =
                    role === 'OPERATOR'
                        ? 'Conta do operador não identificada para registrar no ms-chat.'
                        : 'Conta não identificada. Selecione a conta para registrar no ms-chat.'
                return
            }

            this.socketUserId = userId
            if (!socket.connected) return

            const displayName = this.getSocketDisplayName()
            if (role === 'OPERATOR' && !this.filters?.account) {
                this.filters.account = accountId
            }

            socket.emit('user:register', { userId, accountId, role, clientType: 'ADMIN_MONITOR', displayName }, ack => {
                if (ack?.ok) {
                    this.callStatus = 'Registrado no ms-chat'
                } else if (ack?.error) {
                    this.callStatus = `Falha ao registrar: ${ack.error}`
                }
            })
        },

        normalizeIdCandidate(value) {
            if (value === null || value === undefined) return ''

            if (typeof value === 'string' || typeof value === 'number') {
                const normalized = String(value).trim()
                return normalized || ''
            }

            if (typeof value === 'object') {
                return this.normalizeIdCandidate(value?._id || value?.id || value?.accountId || value?.companyId || value?.userId || '')
            }

            return ''
        },

        firstValidId(...candidates) {
            for (let index = 0; index < candidates.length; index += 1) {
                const resolved = this.normalizeIdCandidate(candidates[index])
                if (resolved) return resolved
            }
            return ''
        },

        decodeSessionTokenPayload() {
            const token = this.$session?.get?.('token')
            if (!token || typeof token !== 'string') return null

            const tokenParts = token.split('.')
            if (tokenParts.length < 2) return null

            try {
                const base64Payload = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/')
                const paddedPayload = base64Payload.padEnd(base64Payload.length + ((4 - (base64Payload.length % 4)) % 4), '=')
                return JSON.parse(window.atob(paddedPayload))
            } catch {
                return null
            }
        },

        getSocketUserId() {
            const tokenPayload = this.decodeSessionTokenPayload()
            const fromUser = this.firstValidId(this.user?._id, this.user?.id, this.user?.userId, tokenPayload?.userId, tokenPayload?.sub, tokenPayload?._id)
            if (fromUser) return String(fromUser)

            const sessionUser = this.$session?.get?.('user')
            return this.firstValidId(sessionUser?._id, sessionUser?.id, sessionUser?.userId, tokenPayload?.userId, tokenPayload?.sub, tokenPayload?._id)
        },

        getSocketAccountId() {
            const tokenPayload = this.decodeSessionTokenPayload()
            const fromUser = this.firstValidId(
                this.user?.account,
                this.user?.accountId,
                this.user?.companyUser?.account,
                this.user?.companyUser?.accountId,
                this.user?.companyUser?.company?.account,
                this.user?.companyUser?.company?.accountId,
                this.user?.companyUser?.company,
                this.user?.company,
                tokenPayload?.account,
                tokenPayload?.accountId,
                tokenPayload?.company,
                tokenPayload?.companyId,
            )
            if (fromUser) return fromUser

            const fromFilter = this.filters?.account || ''
            if (fromFilter) return fromFilter

            const fromSelectedEvent = this.firstValidId(this.selectedEvent?.account, this.selectedEvent?.accountId, this.selectedEvent?.site?.account, this.selectedEvent?.client?.account)
            if (fromSelectedEvent) return fromSelectedEvent

            const sessionUser = this.$session?.get?.('user')
            return this.firstValidId(
                sessionUser?.account,
                sessionUser?.accountId,
                sessionUser?.companyUser?.account,
                sessionUser?.companyUser?.accountId,
                sessionUser?.companyUser?.company?.account,
                sessionUser?.companyUser?.company?.accountId,
                sessionUser?.companyUser?.company,
                sessionUser?.company,
                tokenPayload?.account,
                tokenPayload?.accountId,
                tokenPayload?.company,
                tokenPayload?.companyId,
            )
        },

        getSocketRole() {
            const tokenPayload = this.decodeSessionTokenPayload()
            return String(this.user?.companyUser?.subtype || this.role || tokenPayload?.role || tokenPayload?.subtype || '')
                .trim()
                .toUpperCase()
        },

        getSocketDisplayName() {
            const user = this.user || this.$session?.get?.('user') || {}
            const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim()
            return user?.fullName || fullName || user?.username || user?.email || 'Central'
        },

        getAttendanceOperatorId(attendance) {
            const operator = attendance?.operator
            if (!operator) return ''
            if (typeof operator === 'string') return operator
            return operator?._id || operator?.id || ''
        },

        resolveEventCallTarget(targetType = 'VIGILANT') {
            const vigilant = this.selectedEvent?.vigilant
            const vigilantId = typeof vigilant === 'string' ? vigilant : vigilant?._id || vigilant?.id || ''
            const vigilantName = [vigilant?.firstName, vigilant?.lastName].filter(Boolean).join(' ').trim() || vigilant?.fullName || 'Vigilante'

            const deviceId = this.selectedEvent?.deviceInfo?.deviceId || ''
            const siteName = this.selectedEvent?.site?.name || ''
            const deviceLabelSuffix = siteName ? ` (${siteName})` : ''
            const deviceName = deviceId ? `Dispositivo${deviceLabelSuffix}` : ''

            if (targetType === 'DEVICE') {
                if (!deviceId) return null
                return {
                    userId: String(deviceId),
                    label: deviceName,
                    clientType: 'ALERTPORT_DEVICE',
                }
            }

            if (targetType === 'VIGILANT') {
                if (!vigilantId) return null
                return {
                    userId: String(vigilantId),
                    label: vigilantName,
                    clientType: 'VIGILANT_APP',
                }
            }

            return null
        },

        async registerCallAttendance(notes) {
            if (!this.selectedEvent?._id) return

            const operatorId = await this.getOperatorId()
            const attendancePayload = {
                account: this.selectedEvent?.account?._id,
                client: this.selectedEvent?.client?._id,
                site: this.selectedEvent?.site?._id,
                event: this.selectedEvent?.event?._id,
                type: 'CALL_VIGILANT',
                notes: notes || '',
                patrolAction: this.selectedEvent?._id,
                createDate: moment().utc().format(),
                status: 'ACTIVE',
                siteGroup: this.$session.get('user')?.siteGroup?._id,
                operator: operatorId,
            }

            try {
                await Services.createAttendance(this, attendancePayload)
                const filters = {
                    patrolAction: this.selectedEvent?._id,
                    status: 'ACTIVE',
                }
                this.attendances = await Services.getEventAttendances(this, filters)
            } catch {
                Common.show(this, 'bottom-right', 'danger', 'str.error_creating_attendance')
            }
        },

        async startEventCall(targetType = 'VIGILANT', mode = 'NORMAL') {
            if (this.isStartingEventCall) return
            if (!this.canShowEventCallActions) return

            const target = this.resolveEventCallTarget(targetType)
            if (!target?.userId) {
                this.callStatus = targetType === 'DEVICE' ? 'Dispositivo indisponível para ligação' : 'Vigilante indisponível para ligação'
                return
            }

            this.isStartingEventCall = true
            try {
                const modeLabel = mode === 'SILENT_LISTEN' ? 'Escuta ativa' : 'Ligação'
                const note = `${modeLabel} iniciada para ${target.label}`
                const result = await this.startCall({
                    to: target.userId,
                    toLabel: target.label,
                    peerClientType: target.clientType,
                    callMode: mode,
                    registerAttendanceNote: note,
                })

                if (!result?.ok && result?.error) {
                    this.callStatus = `Falha ao iniciar: ${result.error}`
                }
            } finally {
                this.isStartingEventCall = false
            }
        },

        normalizeCallPeerLabel(value) {
            const text = value ? String(value).trim() : ''
            if (!text) return 'Contato'
            if (/^[a-fA-F0-9]{24}$/.test(text)) return 'Contato'
            return text
        },

        applyIncomingCallPayload(payload = {}, options = {}) {
            const roomId = payload?.roomId || ''
            const from = payload?.from || ''
            const fromClientType = payload?.fromClientType || ''
            const fromRole = payload?.fromRole || ''
            const callMode = payload?.callMode === 'SILENT_LISTEN' ? 'SILENT_LISTEN' : 'NORMAL'
            this.applyCallRecordingPolicy(payload?.callRecordingEnabled)
            const rejectIfBusy = options?.rejectIfBusy !== false
            const clearBridge = options?.clearBridge !== false

            if (!roomId || !from) return

            if (this.activeCallRoomId || this.pendingIncomingCall) {
                const currentRoomId = this.activeCallRoomId || this.pendingIncomingCall?.roomId || ''
                if (String(currentRoomId) === String(roomId)) {
                    return
                }

                this.callStatus = 'Outra chamada recebida enquanto já existe ligação ativa'
                if (rejectIfBusy) {
                    socket.emit('call:reject', { roomId, userId: this.user?._id })
                }
                clearPendingIncomingCall(roomId)
                return
            }

            const resolvedLabel = this.normalizeCallPeerLabel(payload?.fromLabel || payload?.fromDisplayName || from)
            this.activeCallRoomId = roomId
            this.callTargetUserId = from
            this.callMode = callMode
            this.callDuration = 0
            this.incomingCallAccepted = false
            this.callPendingOffer = null
            this.callMicMuted = this.callMode === 'SILENT_LISTEN'
            this.callPeerUserId = from || ''
            this.callPeerLabel = resolvedLabel
            this.callPeerClientType = fromClientType || ''
            this.callPeerRole = fromRole || ''
            this.pendingIncomingCall = { roomId, from, fromLabel: resolvedLabel, fromClientType: fromClientType || '', fromRole: fromRole || '' }
            this.callStatus = `Ligação recebida de ${resolvedLabel}`
            this.startIncomingRingtone()

            if (clearBridge) {
                clearPendingIncomingCall(roomId)
            }
        },

        consumeIncomingCallFromBridge() {
            const pendingIncoming = consumePendingIncomingCall()
            if (!pendingIncoming?.roomId) return
            const normalizedClientType = String(pendingIncoming?.fromClientType || '').toUpperCase()
            if (normalizedClientType.includes('ALERTPORT')) return
            this.applyIncomingCallPayload(pendingIncoming, { rejectIfBusy: true, clearBridge: false })
        },

        resolveCallPeerCategory(clientType, role, userId) {
            const normalizedClientType = (clientType || '').toUpperCase()
            const normalizedRole = (role || '').toUpperCase()
            const normalizedUserId = (userId || '').toUpperCase()

            if (normalizedClientType.includes('ALERTPORT')) return 'ALERTPORT_DEVICE'
            if (normalizedClientType.includes('VIGILANT')) return 'VIGILANT_APP'
            if (normalizedClientType.includes('ADMIN_MONITOR')) return 'ADMIN_MONITOR'
            if (normalizedRole.includes('VIGILANT')) return 'VIGILANT_APP'
            if (normalizedUserId.includes('DEVICE')) return 'ALERTPORT_DEVICE'
            return 'UNKNOWN'
        },

        toggleCallMicMute() {
            if (this.isSilentListenMicLocked) return
            this.callMicMuted = !this.callMicMuted
            if (this.callLocalStream?.getAudioTracks) {
                this.callLocalStream.getAudioTracks().forEach(track => {
                    track.enabled = !this.callMicMuted
                })
            }
        },

        toggleRemoteAudioMute() {
            this.callRemoteMuted = !this.callRemoteMuted
            const audio = this.$refs.remoteAudio
            if (audio) {
                audio.muted = this.callRemoteMuted
            }
        },

        startIncomingRingtone() {
            try {
                if (!this.incomingRingtoneAudio) {
                    this.incomingRingtoneAudio = new Audio(phoneRingtoneUrl)
                    this.incomingRingtoneAudio.loop = true
                    this.incomingRingtoneAudio.preload = 'auto'
                }
                this.incomingRingtoneAudio.volume = 1
                this.incomingRingtoneAudio.currentTime = 0
                const playPromise = this.incomingRingtoneAudio.play()
                if (playPromise?.catch) {
                    playPromise.catch(() => {})
                }
            } catch (e) {
                // ignore ringtone start errors (autoplay policy, etc)
            }
        },

        stopIncomingRingtone() {
            try {
                if (!this.incomingRingtoneAudio) return
                this.incomingRingtoneAudio.pause()
                this.incomingRingtoneAudio.currentTime = 0
            } catch (e) {
                // ignore ringtone stop errors
            }
        },

        async acceptIncomingCall() {
            const pending = this.pendingIncomingCall
            if (!pending) return
            if (this.callActionBusy) return
            if (!socket.connected) {
                this.callStatus = 'Socket desconectado'
                return
            }

            this.callActionBusy = true
            this.stopIncomingRingtone()
            socket.emit('call:accept', { roomId: pending.roomId, userId: this.user?._id }, async ack => {
                if (ack?.error) {
                    this.callStatus = `Falha ao aceitar: ${ack.error}`
                    this.cleanupCall()
                    this.callActionBusy = false
                    return
                }

                this.incomingCallAccepted = true
                this.pendingIncomingCall = null
                this.callStatus = 'Conectando áudio...'
                clearPendingIncomingCall(pending.roomId)

                const pendingOffer = this.callPendingOffer
                this.callPendingOffer = null
                if (pendingOffer && pendingOffer.roomId === pending.roomId) {
                    await this.processOffer(pendingOffer)
                }
                this.callActionBusy = false
            })
        },

        rejectIncomingCall() {
            const pending = this.pendingIncomingCall
            if (!pending) return
            if (this.callActionBusy) return

            this.callActionBusy = true
            this.stopIncomingRingtone()
            clearPendingIncomingCall(pending.roomId)
            socket.emit('call:reject', { roomId: pending.roomId, userId: this.user?._id }, () => {
                this.cleanupCall()
                this.callActionBusy = false
            })
        },

        async processOffer({ roomId, from, sdp }) {
            if (!this.activeCallRoomId || roomId !== this.activeCallRoomId) return
            if (!this.callTargetUserId) this.callTargetUserId = from
            if (!this.callPeerUserId && from) this.callPeerUserId = from

            try {
                await this.ensurePeerConnection()

                const SessionDesc = window.RTCSessionDescription || window.webkitRTCSessionDescription || window.mozRTCSessionDescription
                const remoteOffer = SessionDesc ? new SessionDesc(sdp) : sdp
                await this.callPc.setRemoteDescription(remoteOffer)

                const answer = await this.callPc.createAnswer({ offerToReceiveAudio: true, offerToReceiveVideo: false })
                await this.callPc.setLocalDescription(answer)

                socket.emit('webrtc:answer', { roomId: this.activeCallRoomId, to: this.callTargetUserId, sdp: answer }, offerAck => {
                    if (offerAck?.error) {
                        this.callStatus = `Falha ao enviar answer: ${offerAck.error}`
                    }
                })

                this.callStatus = 'Conectando áudio...'

                const queued = this.callPendingIce
                this.callPendingIce = []
                for (const c of queued) {
                    // eslint-disable-next-line no-await-in-loop
                    const IceCand = window.RTCIceCandidate || window.webkitRTCIceCandidate || window.mozRTCIceCandidate
                    const ice = IceCand ? new IceCand(c) : c
                    // eslint-disable-next-line no-await-in-loop
                    await this.callPc.addIceCandidate(ice)
                }
            } catch (e) {
                this.callStatus = `Erro ao aplicar offer: ${String(e?.message || e)}`
                this.endCall()
            }
        },

        async startCall(options = {}) {
            const targetUserId = options?.to || this.callTargetUserId
            if (!targetUserId) {
                return { ok: false, error: 'MISSING_TARGET' }
            }
            if (!socket.connected) {
                this.callStatus = 'Socket desconectado'
                return { ok: false, error: 'SOCKET_DISCONNECTED' }
            }

            const callMode = options?.callMode || 'NORMAL'
            this.callMode = callMode
                this.callMicMuted = callMode === 'SILENT_LISTEN'
                this.callStatus = callMode === 'SILENT_LISTEN' ? 'Iniciando escuta ativa...' : 'Iniciando chamada...'
                this.callDuration = 0

            const to = targetUserId
            const from = this.user?._id
            const accountId = this.getSocketAccountId()
            const peerClientType = options?.peerClientType || this.callPeerClientType || ''
            const peerLabel = this.normalizeCallPeerLabel(options?.toLabel || '')

            return new Promise(resolve => {
                socket.emit('call:start', { to, from, accountId, callMode }, async ack => {
                    if (!ack?.ok) {
                        this.callStatus = `Falha ao iniciar: ${ack?.error || 'UNKNOWN'}`
                        resolve({ ok: false, error: ack?.error || 'UNKNOWN' })
                        return
                    }

                    this.activeCallRoomId = ack.roomId
                    this.incomingCallAccepted = true
                    this.pendingIncomingCall = null
                    this.callPendingOffer = null
                    if (ack?.to) this.callTargetUserId = ack.to
                    this.callPeerUserId = ack?.to || this.callTargetUserId || ''
                    this.callPeerLabel = this.normalizeCallPeerLabel(ack?.toLabel || peerLabel || this.callTargetUserId)
                    this.callPeerClientType = peerClientType
                    this.callPeerRole = ''
                    this.applyCallRecordingPolicy(ack?.callRecordingEnabled)
                    this.callStatus = callMode === 'SILENT_LISTEN' ? `Escuta ativa iniciada com ${this.callPeerLabel}...` : `Chamando ${this.callPeerLabel}...`
                    if (ack?.targetOnline === false) {
                        this.callStatus = ack?.wakeupTriggered
                            ? 'Dispositivo offline. Push de wake-up enviado e chamada ficou pendente.'
                            : 'Dispositivo offline. Chamada pendente aguardando reconexão.'
                    }

                    if (options?.registerAttendanceNote) {
                        await this.registerCallAttendance(options.registerAttendanceNote)
                    }

                    try {
                        await this.ensurePeerConnection()
                        const offer = await this.callPc.createOffer({ offerToReceiveAudio: true, offerToReceiveVideo: false })
                        await this.callPc.setLocalDescription(offer)
                        socket.emit('webrtc:offer', { roomId: this.activeCallRoomId, to, sdp: offer }, offerAck => {
                            if (offerAck?.error) {
                                this.callStatus = `Falha ao enviar offer: ${offerAck.error}`
                                return
                            }
                            if (offerAck?.queued) {
                                this.callStatus = 'Dispositivo em reconexão. Chamada será entregue quando voltar online...'
                            }
                        })

                        // flush ICE queued before pc ready
                        const queued = this.callPendingIce
                        this.callPendingIce = []
                        for (const c of queued) {
                            // eslint-disable-next-line no-await-in-loop
                            const IceCand = window.RTCIceCandidate || window.webkitRTCIceCandidate || window.mozRTCIceCandidate
                            const ice = IceCand ? new IceCand(c) : c
                            await this.callPc.addIceCandidate(ice)
                        }
                        resolve({ ok: true })
                    } catch (e) {
                        this.callStatus = `Erro WebRTC: ${String(e?.message || e)}`
                        this.endCall()
                        resolve({ ok: false, error: String(e?.message || e) })
                    }
                })
            })
        },

        endCall() {
            const roomId = this.activeCallRoomId
            if (!roomId) return
            if (this.callActionBusy) return
            this.callActionBusy = true
            socket.emit('call:end', { roomId, userId: this.user?._id }, ack => {
                if (ack?.error) {
                    this.callStatus = `Falha ao encerrar: ${ack.error}`
                    this.callActionBusy = false
                }
                this.cleanupCall()
                this.callActionBusy = false
            })
        },

        async cleanupCall() {
            const currentRoomId = this.activeCallRoomId || this.pendingIncomingCall?.roomId || ''
            if (currentRoomId) {
                clearPendingIncomingCall(currentRoomId)
            }
            this.stopIncomingRingtone()

            if (this.callRecordingState?.mediaRecorder) {
                if (socket?.connected && currentRoomId) {
                    const uploadResult = await stopCallRecordingAndUpload(this.callRecordingState, socket, {
                        roomId: currentRoomId,
                        accountId: this.getSocketAccountId(),
                        callMode: this.callMode,
                        initiatedBy: this.user?._id || this.socketUserId || '',
                        peerUserId: this.callPeerUserId || this.callTargetUserId || '',
                        codec: 'opus',
                    })
                    if (!uploadResult?.ok && uploadResult?.error && uploadResult?.error !== 'NO_ACTIVE_RECORDING') {
                        this.callStatus = `Gravação não enviada: ${uploadResult.error}`
                    }
                } else {
                    resetCallRecordingState(this.callRecordingState)
                }
            } else {
                resetCallRecordingState(this.callRecordingState)
            }

            try {
                if (this.callPc) {
                    this.callPc.onicecandidate = null
                    this.callPc.ontrack = null
                    this.callPc.close()
                }
            } catch (e) {
                // ignore
            }

            try {
                if (this.callLocalStream?.getTracks) {
                    this.callLocalStream.getTracks().forEach(t => t.stop())
                }
            } catch (e) {
                // ignore
            }

            try {
                const audio = this.$refs.remoteAudio
                if (audio) {
                    audio.srcObject = null
                    audio.muted = false
                }
            } catch (e) {
                // ignore
            }

            this.callPc = null
            this.callLocalStream = null
            this.callPendingIce = []
            this.callPendingOffer = null
            this.activeCallRoomId = ''
            this.pendingIncomingCall = null
            this.incomingCallAccepted = false
            this.callDuration = 0
            this.callPeerUserId = ''
            this.callPeerLabel = ''
            this.callPeerClientType = ''
            this.callPeerRole = ''
            this.callMicMuted = false
            this.callRemoteMuted = false
            this.callActionBusy = false
            this.callMode = 'NORMAL'
            this.applyCallRecordingPolicy(null)
        },

        async ensurePeerConnection() {
            if (this.callPc) return

            const Pc = window.RTCPeerConnection || window.webkitRTCPeerConnection || window.mozRTCPeerConnection
            if (!Pc) throw new Error('RTCPeerConnection não disponível neste navegador')

            this.callPc = new Pc({
                iceServers: getWebRtcIceServers(),
            })

            this.callPc.onicecandidate = ev => {
                if (!ev.candidate) return
                if (!this.activeCallRoomId || !this.callTargetUserId) return
                const candidate = typeof ev.candidate?.toJSON === 'function' ? ev.candidate.toJSON() : ev.candidate
                socket.emit('webrtc:ice', { roomId: this.activeCallRoomId, to: this.callTargetUserId, candidate }, iceAck => {
                    if (iceAck?.error) {
                        // evita spam no UI; mostra só o último erro relevante
                        this.callStatus = `Falha ICE: ${iceAck.error}`
                    }
                })
            }

            this.callPc.ontrack = ev => {
                // Some implementations don't populate ev.streams; handle track-only events.
                let stream = (ev.streams && ev.streams[0]) || null
                if (!stream && ev.track && window.MediaStream) {
                    stream = new window.MediaStream([ev.track])
                }
                if (!stream) return
                const audio = this.$refs.remoteAudio
                if (audio && audio.srcObject !== stream) {
                    audio.srcObject = stream
                    // Some browsers require explicit play()
                    if (audio.play) audio.play().catch(() => null)
                }

                attachStreamToRecording(this.callRecordingState, stream, 'remote')
                if (this.callLocalStream) {
                    attachStreamToRecording(this.callRecordingState, this.callLocalStream, 'local')
                }
                startCallRecording(this.callRecordingState)
                this.callStatus = 'Áudio remoto conectado'
            }

            if (navigator?.mediaDevices?.getUserMedia) {
                this.callLocalStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false })
                this.callLocalStream.getTracks().forEach(track => {
                    this.callPc.addTrack(track, this.callLocalStream)
                })
                attachStreamToRecording(this.callRecordingState, this.callLocalStream, 'local')
                if (this.callLocalStream?.getAudioTracks) {
                    this.callLocalStream.getAudioTracks().forEach(track => {
                        track.enabled = !(this.callMicMuted || this.isSilentListenMicLocked)
                    })
                }
            } else {
                throw new Error('getUserMedia não disponível')
            }
        },
        async selectEventFromSosNotification({ patrolActionId = '', notificationType = '', siteId = '' } = {}) {
            await this.filter()

            const siteMatches = event => {
                if (!siteId) return true
                const eventSiteId = event?.site?._id || event?.site?.id || event?.site
                return String(eventSiteId || '') === String(siteId)
            }

            if (notificationType) {
                const selectedByTypeAndSite = this.events.find(event => event?.type === notificationType && siteMatches(event))
                if (selectedByTypeAndSite) {
                    await this.handleSelectEvent(selectedByTypeAndSite)
                    return
                }
            }

            if (patrolActionId) {
                const selectedEvent = this.events.find(event => event?._id === patrolActionId)
                if (selectedEvent) {
                    await this.handleSelectEvent(selectedEvent)
                    return
                }

                await this.updateSelectedEvent(patrolActionId)
                if (this.selectedEvent?._id) {
                    const formattedSelectedEvent = await this.formatPatrolActions([this.selectedEvent])
                    if (formattedSelectedEvent?.length > 0) {
                        this.selectedEvent = formattedSelectedEvent[0]
                    }

                    const filters = {
                        patrolAction: this.selectedEvent._id,
                        status: 'ACTIVE',
                    }
                    this.attendances = await Services.getEventAttendances(this, filters)
                    return
                }
            }

            if (!this.selectedEvent && this.events?.length > 0) {
                const firstByType = notificationType ? this.events.find(event => event?.type === notificationType) : null
                await this.handleSelectEvent(firstByType || this.events[0])
            }
        },
    },
    async created() {
        // Inicializa o monitor (configura filtros, dados iniciais, etc.)
        await Controller.init(this)
        await this.filter()
        if (!this.selectedEvent && this.events?.length > 0) {
            this.selectedEvent = this.events[0]
        }
        this.initCallSocket()
        this.$nextTick(() => {
            this.registerOnSocket()
            this.consumeIncomingCallFromBridge()
        })
        this.operatorIncomingCallBridgeUnsubscribe = subscribePendingIncomingCall(payload => {
            if (!payload?.roomId) return
            // Chamadas do alertport-app devem ser exibidas apenas no AlertMonitor
            // (/monitor/alert-occurrence).
            const normalizedClientType = String(payload?.fromClientType || '').toUpperCase()
            if (normalizedClientType.includes('ALERTPORT')) return
            this.applyIncomingCallPayload(payload, { rejectIfBusy: true, clearBridge: false })
        })
        const state = this

        const notificationActions = {
            INCIDENT: () => {
                Common.show(state, 'top-right', 'warn', state.$t('msg.new.incident.notification'))
            },
            SOS_ALERT: () => {
                state.$registerEvent.$emit('soundAlert')
            },
            CRASH: () => {
                Common.show(state, 'top-right', 'warn', state.$t('msg.crash.device.notification'))
            },
            LOWVOLTAGE: () => {
                Common.show(state, 'top-right', 'warn', state.$t('msg.low.voltage.device.notification'))
            },
            CANCEL_PATROL: () => {
                Common.show(state, 'top-right', 'indigo', state.$t('msg.new.canceled.patrol.notification'))
            },
        }

        const failurePatrolActions = {
            EXPIRED: () => {
                Common.show(state, 'top-right', 'warn', state.$t('msg.expired.patrol.notification'))
            },
            NOT_STARTED: () => {
                Common.show(state, 'top-right', 'warn', state.$t('msg.not.started.patrol.notification'))
            },
            INCOMPLETE: () => {
                Common.show(state, 'top-right', 'warn', state.$t('msg.incomplete.patrol.notification'))
            },
        }

        state.$registerEvent.$on('changeLanguage', async function () {
            state.events = await state.formatPatrolActions(state.items)
        })

        const siteIds = await state.$session.get('user')?.siteGroup?.sites
        const siteGroupId = await state.$session.get('user')?.siteGroup?._id
        const role = await state.$session.get('user')?.companyUser?.subtype
        if ((!siteIds || siteIds.length === 0) && role === 'OPERATOR') {
            state.operatorWithoutGroup = true
        }

        if (state.$route?.query?.fromAlert === '1' || state.$route?.query?.fromSos === '1') {
            await state.selectEventFromSosNotification({
                patrolActionId: state.$route?.query?.patrolActionId || '',
                notificationType: state.$route?.query?.notificationType || '',
                siteId: state.$route?.query?.siteId || '',
            })
        }

        if (siteIds && siteIds.length > 0) {
            siteIds.forEach(site => {
                const notificationDocRef = doc(db, 'notifications', site)
                const unsubscribeNotifications = onSnapshot(notificationDocRef, async document => {
                    if (!document.exists) return
                    const data = document.data()
                    if (!data) return
                    // Notificações com origem no alertport-app são tratadas pela tela de alert-occurrence.
                    if (String(data.source || '').toUpperCase() === 'ALERTPORT') {
                        return
                    }
                    const type = data.type
                    if (!state.isMonitorEventTypeEnabled(type)) {
                        await deleteDoc(notificationDocRef)
                        await state.filter()
                        if (!state.selectedEvent) {
                            state.selectedEvent = state.events[0]
                        }
                        return
                    }
                    if (type === 'OUTSIDE_GEOFENCE') {
                        try {
                            await state.handleOutsideGeofenceNotification({
                                ...data,
                                source: data.source || 'SHIELDGO',
                            })
                        } catch (_error) {
                            Common.show(state, 'top-right', 'warn', state.$t('msg.outside.geofence.notification'))
                        } finally {
                            await deleteDoc(notificationDocRef)
                        }
                        return
                    }
                    if (type === 'FAILURE_PATROL') {
                        const subtype = data.subtype
                        if (failurePatrolActions[subtype]) {
                            failurePatrolActions[subtype]()
                        }
                    } else if (notificationActions[type]) {
                        notificationActions[type]()
                    }
                    await deleteDoc(notificationDocRef)
                    await state.filter()
                    if (!state.selectedEvent) {
                        state.selectedEvent = state.events[0]
                    }
                })
                state.subscribes.push(unsubscribeNotifications)

                const unsubscribeUpdatedMedias = onSnapshot(doc(db, 'updatedMedias', site), async document => {
                    const patrolAction = document.data()?.patrolAction
                    const type = document.data()?.type
                    if (patrolAction === state.selectedEvent?._id) {
                        const url = document.data()?.url
                        if (type === 'PHOTO') {
                            state.selectedEvent.photoURL = url
                        } else if (type === 'SIGNATURE') {
                            state.selectedEvent.signatureURL = url
                        } else if (type === 'SOUND') {
                            state.selectedEvent.soundURL = url
                        }
                    }
                    await deleteDoc(doc(db, 'updatedMedias', site))
                })
                state.subscribes.push(unsubscribeUpdatedMedias)
            })
        }

        if (siteGroupId) {
            const unsubscribeAttendanceEventReport = onSnapshot(doc(db, 'updateAttendanceEventReport', siteGroupId), async document => {
                const patrolActionId = document.data()?.patrolActionId
                if (patrolActionId === state.selectedEvent?._id) {
                    const filters = { patrolAction: patrolActionId, status: 'ACTIVE' }
                    state.attendances = await Services.getEventAttendances(state, filters)
                    await deleteDoc(doc(db, 'updateAttendanceEventReport', siteGroupId))
                }
            })
            state.subscribes.push(unsubscribeAttendanceEventReport)

            const unsubscribeCloseAttendanceEvent = onSnapshot(doc(db, 'updateCloseAttendanceEvent', siteGroupId), async document => {
                const patrolActionId = document.data()?.patrolActionId
                if (patrolActionId) {
                    await state.filter()
                    if (patrolActionId === state.selectedEvent?._id) {
                        state.selectedEvent = null
                        state.attendances = []
                    }
                    await deleteDoc(doc(db, 'updateCloseAttendanceEvent', siteGroupId))
                }
            })
            state.subscribes.push(unsubscribeCloseAttendanceEvent)

            const unsubscribeAttendanceEvent = onSnapshot(doc(db, 'updateAttendanceEvent', siteGroupId), async document => {
                if (document?.data()?.attendance) {
                    const attendance = JSON.parse(document.data()?.attendance)
                    const operator = JSON.parse(document.data()?.operator)
                    const patrolActionId = document.data()?.patrolActionId
                    await state.filter()
                    if (patrolActionId === state.selectedEvent?._id) {
                        attendance.operator = operator
                        state.selectedEvent.attendance = attendance
                    }
                    await deleteDoc(doc(db, 'updateAttendanceEvent', siteGroupId))
                }
            })
            state.subscribes.push(unsubscribeAttendanceEvent)
        }
    },
    computed: {
        selectedAttendanceOperatorId() {
            return this.getAttendanceOperatorId(this.selectedEvent?.attendance)
        },

        selectedVigilantUserId() {
            const vigilant = this.selectedEvent?.vigilant
            const vigilantId = typeof vigilant === 'string' ? vigilant : vigilant?._id || vigilant?.id || ''
            return vigilantId ? String(vigilantId) : ''
        },

        isSelectedVigilantOnline() {
            if (!this.selectedVigilantUserId) return false
            const vigilantId = String(this.selectedVigilantUserId)
            return (this.onlineUsers || []).some(userId => String(userId || '') === vigilantId)
        },

        isSelectedEventAttendanceOwner() {
            return Boolean(this.selectedEvent?.attendance?.isAttendance && this.selectedAttendanceOperatorId && this.selectedAttendanceOperatorId === this.user?._id)
        },

        canCallVigilant() {
            return Boolean(this.resolveEventCallTarget('VIGILANT')?.userId)
        },

        canCallDevice() {
            return Boolean(this.resolveEventCallTarget('DEVICE')?.userId)
        },

        canStartSelectedEventAttendance() {
            return Boolean(
                (!this.selectedEvent?.attendance || !this.selectedEvent?.attendance?.isAttendance) &&
                    this.user?.companyUser?.subtype === 'OPERATOR' &&
                    OPERATOR_ATTENDABLE_EVENT_TYPES.includes(this.selectedEvent?.type)
            )
        },

        selectedEventAttendanceButtonClass() {
            return EVENT_ATTENDANCE_BUTTON_CLASS_BY_TYPE[this.selectedEvent?.type] || 'btn-primary'
        },

        canShowEventCallActions() {
            return Boolean(this.isSelectedEventAttendanceOwner && this.selectedEvent?.attendance?.status === 'IN_PROGRESS' && ['SOS_ALERT', 'INCIDENT', 'CANCEL_PATROL'].includes(this.selectedEvent?.type))
        },

        canShowSilentListenAction() {
            return Boolean(this.canShowEventCallActions && this.selectedEvent?.type === 'SOS_ALERT' && this.canCallVigilant)
        },

        isSilentListenMicLocked() {
            return this.callMode === 'SILENT_LISTEN'
        },

        callModalVisible() {
            return Boolean(this.pendingIncomingCall || this.activeCallRoomId)
        },

        isIncomingCallRinging() {
            return Boolean(this.pendingIncomingCall && !this.incomingCallAccepted)
        },

        isCallConnected() {
            return Boolean(this.activeCallRoomId && this.incomingCallAccepted)
        },

        formattedCallDuration() {
            const totalSeconds = Number.isFinite(this.callDuration) ? Math.max(0, Math.floor(this.callDuration)) : 0
            const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0')
            const seconds = String(totalSeconds % 60).padStart(2, '0')
            return `${minutes}:${seconds}`
        },

        callPeerDisplayName() {
            const label = this.callPeerLabel || this.pendingIncomingCall?.fromLabel || this.callPeerUserId || this.pendingIncomingCall?.from || ''
            return this.normalizeCallPeerLabel(label)
        },

        callPeerCategory() {
            const clientType = this.callPeerClientType || this.pendingIncomingCall?.fromClientType || ''
            const role = this.callPeerRole || this.pendingIncomingCall?.fromRole || ''
            const userId = this.callPeerUserId || this.pendingIncomingCall?.from || this.callTargetUserId || ''
            return this.resolveCallPeerCategory(clientType, role, userId)
        },

        callPeerTypeLabel() {
            if (this.callPeerCategory === 'ALERTPORT_DEVICE') return 'Dispositivo / Local (AlertPort)'
            if (this.callPeerCategory === 'VIGILANT_APP') return 'Vigilante (ShieldGo App)'
            if (this.callPeerCategory === 'ADMIN_MONITOR') return 'Operador (Central)'
            return 'Contato da central'
        },

        callPeerIconClass() {
            if (this.callPeerCategory === 'ALERTPORT_DEVICE') return 'fa fa-map-marker'
            if (this.callPeerCategory === 'VIGILANT_APP') return 'fa fa-user'
            if (this.callPeerCategory === 'ADMIN_MONITOR') return 'fa fa-headphones'
            return 'fa fa-phone'
        },

        callConnectionStateLabel() {
            if (this.callMode === 'SILENT_LISTEN') return 'Escuta ativa'
            if (this.isIncomingCallRinging) return 'Chamando'
            if (this.isCallConnected) return 'Em ligação'
            return 'Conectando'
        },

        callModalOverline() {
            if (this.callMode === 'SILENT_LISTEN') return 'Escuta ativa'
            if (this.isIncomingCallRinging) return 'Chamada recebida'
            return 'Chamada em andamento'
        },

        callModalStatusText() {
            if (this.callStatus) return this.callStatus
            if (this.isIncomingCallRinging) return 'Aguardando sua decisão'
            if (this.isCallConnected) return 'Conexão ativa com áudio'
            return 'Conectando chamada...'
        },

        socketMsChatUrlDisplay() {
            if (!this.socketMsChatUrl) return '-'
            if (this.socketMsChatUrl === '__MISSING_MS_CHAT_URL__') {
                return 'NÃO CONFIGURADO (defina VUE_APP_MS_CHAT_URL no deploy)'
            }
            return this.socketMsChatUrl
        },

        onlineUsersFiltered() {
            const me = this.user?._id
            return (this.onlineUsers || []).filter(uid => uid && uid !== me)
        },

        canStartCall() {
            return Boolean(this.socketConnected && this.user?._id && this.callTargetUserId && !this.activeCallRoomId && !this.pendingIncomingCall)
        },

        monitorEventCounters() {
            const counters = {
                total: 0,
                attended: 0,
                sos: 0,
                incident: 0,
                cancelPatrol: 0,
                failurePatrol: 0,
            }

            const events = Array.isArray(this.events) ? this.events : []
            counters.total = events.length

            events.forEach(event => {
                if (event?.attendance?.isAttendance) {
                    counters.attended += 1
                }

                switch (event?.type) {
                    case 'SOS_ALERT':
                        counters.sos += 1
                        break
                    case 'INCIDENT':
                        counters.incident += 1
                        break
                    case 'CANCEL_PATROL':
                        counters.cancelPatrol += 1
                        break
                    case 'FAILURE_PATROL':
                        counters.failurePatrol += 1
                        break
                    default:
                        break
                }
            })

            return counters
        },
        totalEvents() {
            return this.monitorEventCounters.total
        },
        attendedEvents() {
            return this.monitorEventCounters.attended
        },
        sosEvents() {
            return this.monitorEventCounters.sos
        },
        incidentEvents() {
            return this.monitorEventCounters.incident
        },
        cancelPatrolEvents() {
            return this.monitorEventCounters.cancelPatrol
        },
        failurePatrolEvents() {
            return this.monitorEventCounters.failurePatrol
        },
        outsideGeofenceDetails() {
            return this.selectedEvent?.type === 'OUTSIDE_GEOFENCE' ? this.getOutsideGeofenceDetails(this.selectedEvent) : null
        },
        enabledMonitorEventTypes() {
            return Array.isArray(this.monitorEventFilters)
                ? this.monitorEventFilters
                    .filter(item => item?.enabled === true && typeof item?.type === 'string' && item.type.trim().length > 0)
                    .map(item => item.type)
                : []
        },
        displayedEvents() {
            const events = Array.isArray(this.events) ? this.events : []
            if (!this.activeTypeFilter) return events
            if (this.activeTypeFilter === 'ATTENDED') {
                return events.filter(e => e?.attendance?.isAttendance)
            }
            return events.filter(e => e?.type === this.activeTypeFilter)
        },
    },
    watch: {
        '$route.query.openedAt': {
            immediate: false,
            async handler(next, prev) {
                if (!next || next === prev) return
                if (this.$route?.query?.fromIncomingCall === '1') {
                    this.consumeIncomingCallFromBridge()
                    return
                }
                if (this.$route?.query?.fromAlert !== '1' && this.$route?.query?.fromSos !== '1') return
                await this.selectEventFromSosNotification({
                    patrolActionId: this.$route?.query?.patrolActionId || '',
                    notificationType: this.$route?.query?.notificationType || '',
                    siteId: this.$route?.query?.siteId || '',
                })
            },
        },
        'user._id'(next, prev) {
            if (!next || next === prev) return
            if (!this.socketConnected) return
            this.registerOnSocket()
        },
        'user.id'(next, prev) {
            if (!next || next === prev) return
            if (!this.socketConnected) return
            this.registerOnSocket()
        },
        socketConnected(next) {
            if (!next) return
            this.registerOnSocket()
        },
        'filters.account'(next, prev) {
            if (!next || next === prev) return
            if (!this.socketConnected) return
            this.registerOnSocket()
        },
    },

    beforeDestroy() {
        this.cleanupCall()
        if (this.incomingRingtoneAudio) {
            try {
                this.incomingRingtoneAudio.pause()
                this.incomingRingtoneAudio.src = ''
            } catch (e) {
                // ignore
            }
            this.incomingRingtoneAudio = null
        }

        if (this.socketHandlers) {
            socket.off('connect', this.socketHandlers.onConnect)
            socket.off('disconnect', this.socketHandlers.onDisconnect)
            socket.off('user:list', this.socketHandlers.onUserList)
            socket.off('call:duration:tick', this.socketHandlers.onCallTick)
            socket.off('call:accept', this.socketHandlers.onCallAccept)
            socket.off('call:incoming', this.socketHandlers.onCallIncoming)
            socket.off('call:reject', this.socketHandlers.onCallReject)
            socket.off('call:end', this.socketHandlers.onCallEnd)
            socket.off('webrtc:offer', this.socketHandlers.onOffer)
            socket.off('webrtc:answer', this.socketHandlers.onAnswer)
            socket.off('webrtc:ice', this.socketHandlers.onIce)
            this.socketHandlers = null
        }

        if (this.operatorIncomingCallBridgeUnsubscribe) {
            this.operatorIncomingCallBridgeUnsubscribe()
            this.operatorIncomingCallBridgeUnsubscribe = null
        }

        this.subscribes.forEach(unsubscribe => unsubscribe())
    },
}
</script>

<style lang="scss" scoped>
.form-check {
    display: inline-block !important;
}
.result-info {
    padding: 1.171875rem;
    position: relative;
    flex: 1;
}
.result-info .btn-row {
    display: flex;
    flex-wrap: wrap;
}
.result-info .btn-row a {
    color: var(--app-component-color);
    background: rgba(var(--app-component-color-rgb), 0.15);
    font-size: 0.875rem;
    padding: 0.3121875rem 0.46875rem;
    border-radius: 4px;
}
.result-info .btn-row a + a {
    margin-left: 0.3121875rem;
}
.panel-body {
    max-height: 80vh !important;
    overflow-y: auto !important;
}
.panel-attendance-body {
    max-height: 88vh !important;
    overflow-y: auto !important;
}
.vigilant-connection-badge {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    font-size: 0.75rem;
    font-weight: 700;
    padding: 0.15rem 0.5rem;
    border-radius: 999px;
}
.vigilant-connection-badge i {
    font-size: 0.5rem;
}
.vigilant-connection-badge.is-online {
    color: #0b5137;
    background: #d1fae5;
}
.vigilant-connection-badge.is-offline {
    color: #6b1020;
    background: #fde2e7;
}
.overflow-y {
    overflow-y: auto !important;
}
.info-result {
    color: var(--app-component-color);
    font-weight: 500;
}
.userimage {
    width: 40px;
    height: 40px;
    overflow: hidden;
    border-radius: 36px;
    margin-right: 0.75rem;
    object-fit: contain;
}
.info-title {
    margin-top: 0;
    margin-bottom: 10px;
    font-size: 18px;
    font-weight: bold;
    color: #6e6e6e !important;
}
.timeline-header {
    padding: 0.2rem;
    display: flex;
    align-items: center;
}
.disabled-icon {
    color: #ccc;
}
.small-icon {
    font-size: 0.5rem;
    margin: 0 0rem;
    color: var(--app-component-color);
}
.pd-0 {
    padding-top: 0;
}
.dot {
    position: absolute;
    bottom: 0.8rem;
    right: 0.8rem;
}
.animated-dot {
    animation: blinker 1s linear infinite;
}
.dot-color {
    color: lightskyblue;
    font-size: 0.5rem;
}
@keyframes blinker {
    50% {
        opacity: 0;
    }
}
img {
    max-width: 100% !important;
    display: block;
}
.center-spinner {
    margin-bottom: 20px;
}
.list-group-selected {
    color: var(--app-component-hover-color);
    background-color: var(--app-component-hover-bg);
}
.no-events {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 20px;
    text-align: center;
}
.no-events h3 {
    margin-top: 0;
    margin-bottom: 10px;
    font-size: 18px;
    font-weight: bold;
    color: #333;
}
.no-events p {
    color: #777;
    margin-bottom: 20px;
}
.no-events-icon {
    width: 80px;
    height: 80px;
}
.stats-container {
    display: flex;
    justify-content: center;
    align-items: center;
    /* Remova ou comente o gap, pois usaremos borda para separar os itens */
    /* gap: 2rem; */
}

/* Cada “coluna” de estatística, agora com borda à direita */
.stat-item {
    display: flex;
    align-items: baseline;
    padding: 0 1rem; /* Espaçamento interno horizontal */
    border-right: 1px solid #555; /* Barra de separação entre itens */
    color: #fff;
}

/* Remove a borda do último item */
.stat-item:last-child {
    border-right: none;
}

/* Rótulo (descrição da estatística) */
.stat-label {
    font-size: 0.725rem;
    font-weight: 400;
    color: #9fa6b2;
    margin-right: 0.25rem;
}

/* Valor (número) da estatística */
.stat-value {
    font-size: 0.725rem;
    font-weight: 400;
    color: #fff;
}

/* Cores específicas por tipo (opcional) */
.stat-total .stat-value {
    color: #ffffff;
}

.stat-attended .stat-value {
    color: #4caf50; /* Verde */
}

.stat-sos .stat-value {
    color: #ff4444; /* Vermelho */
}

.stat-incident .stat-value {
    color: #ffbb33; /* Laranja */
}
.filter-title {
    flex: 0 0 auto;
    text-align: left;
}

.statsbar-wrapper {
    flex: 1;
}

.loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
}

.loading-icon {
    font-size: 2.25rem;
    color: #fff;
}

.event-call-card {
    border-radius: 14px;
    border: 1px solid rgba(189, 209, 246, 0.28);
    background: linear-gradient(160deg, rgba(17, 29, 51, 0.9), rgba(11, 21, 40, 0.92));
    padding: 0.78rem;
}

.event-call-card-header {
    margin-bottom: 0.58rem;
}

.event-call-card-title {
    display: block;
    font-size: 0.78rem;
    font-weight: 700;
    color: #f7f9fd;
}

.event-call-card-subtitle {
    display: block;
    margin-top: 0.08rem;
    color: #d8dee9;
    font-size: 0.66rem;
}

.event-call-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.48rem;
}

.event-call-btn {
    min-height: 44px;
    border: none;
    border-radius: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
    font-size: 0.74rem;
    font-weight: 700;
    padding: 0.45rem 0.5rem;
    transition: transform 0.12s ease, box-shadow 0.12s ease;
}

.event-call-btn:hover {
    transform: translateY(-1px);
}

.event-call-btn:disabled {
    opacity: 0.62;
    cursor: not-allowed;
    transform: none;
}

.event-call-btn.primary {
    background: linear-gradient(145deg, #1e88ff, #2f67ff);
    color: #fff;
    box-shadow: 0 8px 18px rgba(31, 109, 255, 0.27);
}

.event-call-btn.info {
    background: linear-gradient(145deg, #15b7d7, #1297c6);
    color: #fff;
    box-shadow: 0 8px 18px rgba(17, 155, 196, 0.25);
}

.event-call-btn.warning {
    background: linear-gradient(145deg, #ffb03a, #ff8b2b);
    color: #fff;
    box-shadow: 0 8px 18px rgba(255, 142, 46, 0.23);
}

.call-modal-fade-enter-active,
.call-modal-fade-leave-active {
    transition: opacity 0.18s ease;
}

.call-modal-fade-enter,
.call-modal-fade-leave-to {
    opacity: 0;
}

.call-modal-backdrop {
    position: fixed;
    inset: 0;
    z-index: 10050;
    background: rgba(9, 16, 27, 0.62);
    backdrop-filter: blur(4px);
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 1rem;
}

.call-modal-shell {
    width: 100%;
    max-width: 460px;
}

.call-modal-card {
    position: relative;
    overflow: hidden;
    border-radius: 20px;
    padding: 1.1rem;
    background: linear-gradient(170deg, rgba(10, 20, 40, 0.98), rgba(16, 33, 64, 0.98));
    border: 1px solid rgba(139, 179, 255, 0.24);
    box-shadow: 0 24px 44px rgba(0, 0, 0, 0.42);
}

.call-modal-card.is-ringing {
    animation: call-ring-pulse 1.4s infinite ease-in-out;
}

.call-modal-glow {
    position: absolute;
    top: -40%;
    right: -30%;
    width: 240px;
    height: 240px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(45, 148, 255, 0.34), transparent 68%);
    pointer-events: none;
}

.call-modal-header {
    position: relative;
    display: flex;
    align-items: center;
    gap: 0.8rem;
}

.call-modal-avatar {
    width: 52px;
    height: 52px;
    border-radius: 999px;
    background: linear-gradient(145deg, rgba(77, 176, 255, 0.35), rgba(54, 110, 255, 0.72));
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.1rem;
    box-shadow: 0 8px 22px rgba(45, 113, 255, 0.42);
    flex: 0 0 auto;
}

.call-modal-header-info {
    min-width: 0;
    flex: 1;
}

.call-modal-overline {
    margin: 0;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.09em;
    color: rgba(188, 214, 255, 0.88);
}

.call-modal-name {
    margin: 0.18rem 0 0;
    color: #ffffff;
    font-size: 1.1rem;
    line-height: 1.2;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.call-modal-peer-type {
    margin: 0.18rem 0 0;
    color: rgba(223, 233, 255, 0.85);
    font-size: 0.78rem;
}

.call-modal-state {
    border-radius: 999px;
    border: 1px solid rgba(158, 196, 255, 0.38);
    background: rgba(104, 157, 255, 0.16);
    color: #dce9ff;
    padding: 0.22rem 0.55rem;
    font-size: 0.68rem;
    letter-spacing: 0.02em;
    white-space: nowrap;
}

.call-modal-state.ringing {
    border-color: rgba(255, 184, 114, 0.48);
    background: rgba(255, 163, 56, 0.18);
    color: #ffd7a8;
}

.call-modal-state.connected {
    border-color: rgba(101, 221, 160, 0.56);
    background: rgba(68, 189, 123, 0.18);
    color: #9df5c3;
}

.call-modal-body {
    position: relative;
    margin-top: 1rem;
    padding: 0.8rem 0.2rem;
    border-top: 1px solid rgba(164, 198, 255, 0.18);
    border-bottom: 1px solid rgba(164, 198, 255, 0.18);
    text-align: center;
}

.call-modal-timer {
    color: #ffffff;
    font-size: 1.66rem;
    font-weight: 700;
    letter-spacing: 0.07em;
    line-height: 1;
}

.call-modal-status {
    margin-top: 0.42rem;
    font-size: 0.8rem;
    color: rgba(219, 233, 255, 0.92);
}

.call-modal-actions {
    margin-top: 1rem;
    display: grid;
    gap: 0.72rem;
    grid-template-columns: repeat(2, minmax(0, 1fr));
}

.call-modal-actions.active-call {
    grid-template-columns: repeat(3, minmax(0, 1fr));
}

.call-action-btn {
    border: none;
    border-radius: 12px;
    min-height: 72px;
    padding: 0.58rem;
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.34rem;
    transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
    font-size: 0.74rem;
    font-weight: 600;
    letter-spacing: 0.01em;
}

.call-action-btn i {
    font-size: 1.02rem;
}

.call-action-btn:hover {
    transform: translateY(-1px);
}

.call-action-btn:disabled {
    opacity: 0.62;
    cursor: not-allowed;
    transform: none;
}

.call-action-btn.accept {
    color: #0d3d20;
    background: linear-gradient(145deg, #8ef0b7, #58d788);
    box-shadow: 0 10px 24px rgba(67, 198, 121, 0.38);
}

.call-action-btn.reject,
.call-action-btn.end {
    color: #ffe7ea;
    background: linear-gradient(145deg, #ff5e7f, #ef365c);
    box-shadow: 0 10px 24px rgba(238, 58, 93, 0.35);
}

.call-action-btn.end i {
    transform: rotate(135deg);
}

.call-action-btn.control {
    color: #eaf2ff;
    background: linear-gradient(145deg, rgba(111, 161, 255, 0.34), rgba(90, 130, 234, 0.22));
    border: 1px solid rgba(143, 183, 255, 0.3);
}

.call-action-btn.control.active {
    color: #0f2943;
    background: linear-gradient(145deg, #9ed0ff, #78b5ff);
    box-shadow: 0 10px 20px rgba(85, 162, 255, 0.33);
}

@keyframes call-ring-pulse {
    0%,
    100% {
        box-shadow: 0 24px 44px rgba(0, 0, 0, 0.42);
    }
    50% {
        box-shadow: 0 28px 52px rgba(40, 123, 255, 0.34);
    }
}

@media (max-width: 576px) {
    .call-modal-shell {
        max-width: 100%;
    }

    .call-modal-actions.active-call {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

/* Monitor sticky header */
.monitor-sticky-header {
    position: sticky;
    top: 60px;
    z-index: 100;
    background: var(--app-header-bg, #1d2226);
    padding: 0.5rem 0;
    margin-bottom: 0.5rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.monitor-sticky-inner {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
}

.monitor-title {
    flex: 0 0 auto;
    white-space: nowrap;
}

.monitor-statsbar-inline {
    flex: 1 1 0;
    min-width: 0;
}
</style>
